/**
 * Turning Firebase's error codes into sentences, and making sure they escape.
 *
 * The sign-in screen has an error banner and has never been able to show one.
 * Every method in AuthContext caught its own failure, wrote it to the console
 * and returned null, so nothing was ever thrown, LoginPage's catch never ran,
 * and `setError` was never called. Pressing Sign up with an address that
 * already had an account did exactly what pressing it with a working one did:
 * the button un-greyed and the form sat there.
 *
 * Rethrowing alone would fix the silence and replace it with
 * "Firebase: Error (auth/email-already-in-use)." — accurate, and no help to
 * somebody who just wants to get into their garden. So the codes worth
 * naming are named, and everything else falls back to a general apology.
 *
 * Who uses it: AuthContext (every sign-in method rethrows through
 * throwReadable, and a failed redirect becomes redirectError), which
 * LoginPage then shows. The sentences are auth.errors.* in the locale files.
 *
 * @module authErrors
 */

import { t } from '@/shared/i18n/i18nSetup';

/** Firebase code to translation key, for the failures people actually hit. */
const MESSAGES: Record<string, string> = {
  'auth/email-already-in-use': 'auth.errors.emailInUse',
  'auth/invalid-email': 'auth.errors.invalidEmail',
  'auth/weak-password': 'auth.errors.weakPassword',
  'auth/missing-password': 'auth.errors.missingPassword',

  // Firebase deliberately blurs "no such account" and "wrong password" so a
  // stranger cannot use the form to discover who has an account. Saying the
  // same thing for all three keeps that property.
  'auth/wrong-password': 'auth.errors.badCredentials',
  'auth/user-not-found': 'auth.errors.badCredentials',
  'auth/invalid-credential': 'auth.errors.badCredentials',

  'auth/user-disabled': 'auth.errors.disabled',
  'auth/too-many-requests': 'auth.errors.tooMany',
  'auth/network-request-failed': 'auth.errors.network',
  'auth/operation-not-allowed': 'auth.errors.notAllowed',
  'auth/unauthorized-domain': 'auth.errors.unauthorizedDomain',
  // The email has an account by another way; the sign-in is kept and joined
  // when they use that way (AuthContext, pending link).
  'auth/account-exists-with-different-credential': 'auth.errors.otherMethod',
  'auth/popup-blocked': 'auth.errors.popupBlocked',
};

/**
 * Codes that mean "the person changed their mind", not "something broke".
 *
 * Closing the Google window is a decision, and answering it with a red banner
 * tells somebody off for doing what they meant to do.
 */
const CANCELLED = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

/**
 * Whether the failure was the person backing out.
 *
 * @param error - Whatever Firebase threw.
 * @returns True when it was.
 */
export function isCancellation(error: unknown): boolean {
  const code = (error as { code?: string })?.code;
  return typeof code === 'string' && CANCELLED.has(code);
}

/**
 * A sentence a gardener can act on.
 *
 * @param error - Whatever Firebase threw.
 * @returns Readable text, already translated.
 */
export function authErrorMessage(error: unknown): string {
  const code = (error as { code?: string })?.code;
  const key = typeof code === 'string' ? MESSAGES[code] : undefined;
  if (key) return t(key);

  // An unmapped failure still has to be actionable. The first version of this
  // returned a bare "An error occurred during authentication", which is the
  // same dead end as the silence it replaced — it tells somebody that
  // something went wrong and gives them, and anyone helping them, nothing to
  // go on. The code is ugly and it is also the only fact available, so it
  // goes on the end where it can be read out or searched for.
  const detail = typeof code === 'string' && code
    ? code
    : (error as { message?: string })?.message;

  return detail ? `${t('auth.authError')} (${detail})` : t('auth.authError');
}

/**
 * Rethrows a Firebase failure as something worth putting on screen.
 *
 * The original is kept as `cause` so the console still has the real code to
 * debug with — the point is to stop hiding it, not to throw it away.
 *
 * @param error - Whatever Firebase threw.
 * @throws {Error} Always.
 */
export function throwReadable(error: unknown): never {
  const readable = new Error(authErrorMessage(error));
  (readable as Error & { cause?: unknown }).cause = error;
  (readable as Error & { code?: string }).code = (error as { code?: string })?.code;
  throw readable;
}
